/**
 * api.js — All API Calls
 *
 * PURPOSE:
 *   Single file for every fetch() call the widget makes.
 *   No fetch calls anywhere else in the codebase.
 *   All calls include X-Tenant-ID header automatically.
 *
 * HOW IT WORKS:
 *   apiFetch(path, options) wraps fetch() with headers.
 *   Each exported function calls apiFetch() with a path.
 *   Caller handles errors — api.js never swallows them.
 *
 * BE DEPENDENCY:
 *   All endpoints require BE to be deployed.
 *   Each function's comment names the BE endpoint.
 *
 * STUB STATUS:
 *   All functions are implemented and ready.
 *   They all work once BE endpoints are live.
 *
 * USAGE:
 *   import * as api from './core/api.js';
 *   const data = await api.personalizeChat({ entryPoint });
 */

import { getStateSnapshot as getState } from './state.js';
import { API__BASE_URL } from './env.js';

// ─── BASE FETCHER ──────────────────────────────────────────
// All requests go through this. Adds tenant ID header.
async function apiFetch(path, options = {}) {
  const url = `${API__BASE_URL}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    // Tenant identification — required by BE middleware
    'X-Tenant-ID': getState().tenant?.tenant_id ?? '',
    ...options.headers,
  };

  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    // Surface the BE/C7 error so callers (e.g. checkout) can show field-level
    // messages like "phone must be in E.164 format" instead of a generic error.
    let body = null;
    let serverMsg = '';
    try {
      body = await res.clone().json();
      serverMsg = body?.error || body?.message || body?.data?.error || '';
    } catch {
      /* non-JSON error body — fall back to the status line */
    }
    const err = new Error(
      serverMsg || `[RedConcierge API] ${options.method ?? 'GET'} ${path} failed with status ${res.status}`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res;
}

// Unwrap the BE CustomResponse envelope: { message, data } -> data.
function unwrap(body) {
  return body && typeof body === 'object' && 'data' in body ? body.data : body;
}

// Food pairings / recipes may use CustomResponse with an extra nested `data`
// layer ({ success, message, data: { status, pairings, ... } }). Peel until
// domain fields (status, pairings, recipe, …) are reached.
function unwrapNestedData(body) {
  if (!body || typeof body !== 'object') return body;

  const hasDomainFields = (obj) =>
    obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    ('status' in obj ||
      'pairings' in obj ||
      'blurb' in obj ||
      'recipe' in obj ||
      'cover_image_link' in obj ||
      'tasting_notes' in obj ||
      'unique_id' in obj);

  let payload = body;
  for (let depth = 0; depth < 2; depth++) {
    if (payload && typeof payload === 'object' && 'data' in payload && payload.data != null) {
      const inner = payload.data;
      if (typeof inner === 'object' && !Array.isArray(inner)) {
        if (hasDomainFields(inner)) return inner;
        payload = inner;
        continue;
      }
    }
    break;
  }
  return payload;
}

// Feature-card documents store wine fields under json_data; merge for drawer/UI.
export function flattenCardDetails(card) {
  if (!card || typeof card !== 'object') return card;
  const json = card.json_data;
  if (!json || typeof json !== 'object') return { ...card };
  const { json_data: _json, ...rest } = card;
  return { ...json, ...rest };
}

// ─── SESSION ───────────────────────────────────────────────
// BE endpoint: GET /api/v1/chat/personalize/
// Initializes session, returns { session_id, is_chat_personalized }
export async function personalizeChat({ entryPoint, tableNo, isQR }) {
  const params = new URLSearchParams({
    entry_point: entryPoint ?? '',
    table_no: tableNo ?? '',
    is_qr: isQR ?? false,
  });
  const res = await apiFetch(`/api/v1/chat/personalize/?${params}`);
  return unwrap(await res.json());
}

// ─── RESPONSE TYPE ─────────────────────────────────────────
// BE endpoint: GET /api/v1/chat/get-response-type/
// Classifies reply: { is_text_output, type?, introductory_message?, feature_cards? }
export async function getResponseType(params) {
  const query = new URLSearchParams(params);
  const res = await apiFetch(`/api/v1/chat/get-response-type/?${query}`);
  return unwrap(await res.json());
}

// ─── STREAMING CHAT (SSE) ──────────────────────────────────
// BE endpoint: GET /api/v1/chat/stream-reply/ (text/event-stream)
// Returns the raw Response — caller reads response.body for the SSE stream.
export async function getStreamingResponse(params) {
  const query = new URLSearchParams(params);
  return apiFetch(`/api/v1/chat/stream-reply/?${query}`);
}

// ─── CONVERSATION HISTORY ──────────────────────────────────
// BE endpoint: GET /api/v1/chat/conversation-history/
export async function getConversationHistory(sessionId) {
  const res = await apiFetch(
    `/api/v1/chat/conversation-history/?session_id=${sessionId}`
  );
  return unwrap(await res.json());
}

// BE endpoint: POST /api/v1/chat/conversation-history/
export async function saveConversationHistory({
  sessionId,
  conversationHistory,
  tableNo,
}) {
  return apiFetch('/api/v1/chat/conversation-history/', {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      conversation_history: conversationHistory,
      table_no: tableNo,
    }),
  });
}

// Beforeunload variant — fetch keepalive so it survives the page unload.
// Same keepalive trick saveAnalyticsBeacon uses below; preserves X-Tenant-ID.
export function saveConversationHistoryBeacon({
  sessionId,
  conversationHistory,
  tableNo,
}) {
  const url = `${API__BASE_URL}/api/v1/chat/conversation-history/`;
  const body = JSON.stringify({
    session_id: sessionId,
    conversation_history: conversationHistory,
    table_no: tableNo,
  });
  const headers = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': getState().tenant?.tenant_id ?? '',
  };
  try {
    return fetch(url, { method: 'POST', headers, body, keepalive: true });
  } catch {
    return apiFetch('/api/v1/chat/conversation-history/', { method: 'POST', body });
  }
}

// ─── ANALYTICS ─────────────────────────────────────────────
// BE endpoint: POST /api/v1/chat/save-analytics/
// Accepts: session_id, user_agent, latitude, longitude, time_spent,
// clicked_on_banner, entry_point_click_counter, cta_link, waiting_message_*, …
export async function saveAnalytics(data) {
  return apiFetch('/api/v1/chat/save-analytics/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// BE endpoint: POST /api/v1/chat/update-analytics/
// Body: { session_id, update_guided_flows: [name, …] } — counts guided-flow
// button clicks. Old approximated this via save_analytics; the new BE has a
// dedicated endpoint so we use it.
export async function updateAnalytics({ sessionId, updateGuidedFlows }) {
  return apiFetch('/api/v1/chat/update-analytics/', {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      update_guided_flows: updateGuidedFlows,
    }),
  });
}

// Beforeunload variant of saveAnalytics — uses fetch with keepalive so the
// request survives the page unloading. We use fetch (not navigator.sendBeacon)
// so the X-Tenant-ID header still rides along, which sendBeacon can't set.
export function saveAnalyticsBeacon(data) {
  const url = `${API__BASE_URL}/api/v1/chat/save-analytics/`;
  const body = JSON.stringify(data);
  const headers = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': getState().tenant?.tenant_id ?? '',
  };
  try {
    return fetch(url, { method: 'POST', headers, body, keepalive: true });
  } catch {
    // keepalive unsupported or body too large — fall back to a normal POST.
    return apiFetch('/api/v1/chat/save-analytics/', { method: 'POST', body });
  }
}

// ─── FEEDBACK ──────────────────────────────────────────────
// BE endpoint: POST /save_user_feedback
export async function saveUserFeedback({
  sessionId,
  rating,
  message,
  email,
}) {
  return apiFetch('/save_user_feedback', {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      user_feedback_rating: rating,
      user_feedback_message: message,
      user_email: email,
    }),
  });
}

// ─── QUESTION RECOMMENDATIONS ──────────────────────────────
// BE endpoint: GET /api/v1/chat/question-recommendations/
// Returns { continuing_question, give_possible_response_options, possible_response_options[] }
export async function getQuestionRecommendations(sessionId) {
  const res = await apiFetch(
    `/api/v1/chat/question-recommendations/?session_id=${sessionId}`
  );
  return unwrap(await res.json());
}

// ─── AUTHENTICATION ────────────────────────────────────────
// BE endpoint: GET /authenticate_user
// Used only if tenant.ui_features.password_protected is true
export async function authenticateUser(password) {
  const res = await apiFetch(
    `/authenticate_user?password=${encodeURIComponent(password)}`
  );
  return res.json();
}

// ─── SHOPIFY INTEGRATION: cart + account ───────────────────
// BE: /api/v1/integrations/shopify/* — public (no auth); tenant resolved from
// the X-Tenant-ID header apiFetch already sends. Payloads are camelCase to
// match the DRF serializers (serializers.py). Used only when the tenant's
// cart/account features are enabled (Fogarty).

// GET customer/?customerId=  → mapped customer ({ firstName, clubs, ... })
export async function getCustomer(customerId) {
  const res = await apiFetch(
    `/api/v1/integrations/shopify/customer/?customerId=${encodeURIComponent(customerId)}`
  );
  return unwrap(await res.json());
}

// GET cart/?cartId=  → { cartId, items[], cart{} }
export async function getCart(cartId) {
  const res = await apiFetch(
    `/api/v1/integrations/shopify/cart/?cartId=${encodeURIComponent(cartId)}`
  );
  return unwrap(await res.json());
}

// POST cart/create/  { variantId?, quantity?, sellingPlanId? }  → full cart
export async function createCart({
  variantId = null,
  quantity = 1,
  sellingPlanId = null,
} = {}) {
  const body = { variantId, quantity };
  if (sellingPlanId) body.sellingPlanId = sellingPlanId;
  const res = await apiFetch('/api/v1/integrations/shopify/cart/create/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return unwrap(await res.json());
}

// POST cart/add/  { cartId, variantId, quantity, sellingPlanId? }  → full cart
export async function addCartItem({
  cartId,
  variantId,
  quantity = 1,
  sellingPlanId = null,
}) {
  const body = { cartId, variantId, quantity };
  if (sellingPlanId) body.sellingPlanId = sellingPlanId;
  const res = await apiFetch('/api/v1/integrations/shopify/cart/add/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return unwrap(await res.json());
}

// POST cart/remove/  { cartId, lineIds?, variantIds? }  → full cart
export async function removeCartItem({ cartId, lineIds, variantIds }) {
  const res = await apiFetch('/api/v1/integrations/shopify/cart/remove/', {
    method: 'POST',
    body: JSON.stringify({ cartId, lineIds, variantIds }),
  });
  return unwrap(await res.json());
}

// POST cart/update/  { cartId, lines: [{ id?, variantId?, quantity }] }  → full cart
export async function updateCartItem({ cartId, lines }) {
  const res = await apiFetch('/api/v1/integrations/shopify/cart/update/', {
    method: 'POST',
    body: JSON.stringify({ cartId, lines }),
  });
  return unwrap(await res.json());
}

// POST checkout/  { cartId, type, email?, address?, customerId?, discountCode?, shippingMethod? }
export async function checkout(payload) {
  const res = await apiFetch('/api/v1/integrations/shopify/checkout/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return unwrap(await res.json());
}

// GET delivery-countries/  → { countries: [...] }
export async function getDeliveryCountries() {
  const res = await apiFetch('/api/v1/integrations/shopify/delivery-countries/');
  return unwrap(await res.json());
}

// GET login-greeting/?customerId=&sessionId=  → { message, firstName, is_club_member }
export async function getLoginGreeting(customerId, sessionId = '') {
  const params = new URLSearchParams({ customerId, sessionId });
  const res = await apiFetch(
    `/api/v1/integrations/shopify/login-greeting/?${params}`
  );
  return unwrap(await res.json());
}

// ─── COMMERCE7 INTEGRATION: customer + cart + checkout + clubs ──────────────
// BE: /api/v1/integrations/commerce7/* — public (no auth); tenant resolved from
// the X-Tenant-ID header apiFetch already sends. Unlike Shopify, payloads are
// snake_case to match the DRF serializers (commerce7/serializers.py), and the
// cart is bound to a logged-in customer (customer_token). Used only when the
// tenant's commerce7 feature is enabled (Valle della Pace).

const C7 = '/api/v1/integrations/commerce7';

// POST customer/login/  { email, password, session_id }  → customer profile + token
export async function c7Login({ email, password, sessionId }) {
  const res = await apiFetch(`${C7}/customer/login/`, {
    method: 'POST',
    body: JSON.stringify({ email, password, session_id: sessionId }),
  });
  return unwrap(await res.json());
}

// GET customer/self/?customer_token=  → mapped customer profile
export async function c7GetCustomerSelf(customerToken) {
  const params = new URLSearchParams({ customer_token: customerToken });
  const res = await apiFetch(`${C7}/customer/self/?${params}`);
  return unwrap(await res.json());
}

// GET cart/items/?cart_id=  → { cartId, items[], cart{} }
// Anonymous cart — identified by cart_id (App Basic Auth on the BE), no token.
export async function c7GetCart({ cartId } = {}) {
  const params = new URLSearchParams({ cart_id: cartId });
  const res = await apiFetch(`${C7}/cart/items/?${params}`);
  return unwrap(await res.json());
}

// POST cart/create/  { channel }  → cart
export async function c7CreateCart({ channel = 'Web' } = {}) {
  const res = await apiFetch(`${C7}/cart/create/`, {
    method: 'POST',
    body: JSON.stringify({ channel }),
  });
  return unwrap(await res.json());
}

// POST cart/item/add/  { cart_id, sku, quantity }  → cart
// Anonymous add — cart_id + sku + quantity (App Basic Auth on the BE).
export async function c7AddCartItem({ cartId, sku, quantity = 1 }) {
  const res = await apiFetch(`${C7}/cart/item/add/`, {
    method: 'POST',
    body: JSON.stringify({ cart_id: cartId, sku, quantity }),
  });
  return unwrap(await res.json());
}

// POST cart/item/update/  { cart_id, sku, quantity }  → cart
export async function c7UpdateCartItem({ cartId, sku, quantity }) {
  const res = await apiFetch(`${C7}/cart/item/update/`, {
    method: 'POST',
    body: JSON.stringify({ cart_id: cartId, sku, quantity }),
  });
  return unwrap(await res.json());
}

// POST cart/item/remove/  { cart_id, sku }  → cart
export async function c7RemoveCartItem({ cartId, sku }) {
  const res = await apiFetch(`${C7}/cart/item/remove/`, {
    method: 'POST',
    body: JSON.stringify({ cart_id: cartId, sku }),
  });
  return unwrap(await res.json());
}

// GET utils/shipping-regions/  → [ { country, countryCode, states: [{stateName, stateCode}] }, ... ]
export async function c7GetShippingRegions() {
  const res = await apiFetch(`${C7}/utils/shipping-regions/`);
  return unwrap(await res.json());
}

// GET cart/pickup-locations/  → { pickupLocations: [...] }
export async function c7GetPickupLocations() {
  const res = await apiFetch(`${C7}/cart/pickup-locations/`);
  return unwrap(await res.json());
}

// POST cart/proceed_checkout/  { cart_id, payload, customer_token? }  → cart
export async function c7ProceedCheckout({ cartId, payload, customerToken = '' }) {
  const res = await apiFetch(`${C7}/cart/proceed_checkout/`, {
    method: 'POST',
    body: JSON.stringify({ cart_id: cartId, payload, customer_token: customerToken }),
  });
  return unwrap(await res.json());
}

// POST cart/charge/  { cart_id, checkout_type, tender_payload, customer_id?, customer_token? }  → order
export async function c7ChargeCheckout({ cartId, checkoutType, tenderPayload, customerId = '', customerToken = '' }) {
  const res = await apiFetch(`${C7}/cart/charge/`, {
    method: 'POST',
    body: JSON.stringify({
      cart_id: cartId,
      checkout_type: checkoutType,
      tender_payload: tenderPayload,
      customer_id: customerId,
      customer_token: customerToken,
    }),
  });
  return unwrap(await res.json());
}

// GET customer/saved-cards/?customer_id=  → { savedCards: [...] }
export async function c7GetSavedCards(customerId) {
  const params = new URLSearchParams({ customer_id: customerId });
  const res = await apiFetch(`${C7}/customer/saved-cards/?${params}`);
  return unwrap(await res.json());
}

// POST customer/credit-card/add/  { customer_id, card_details, customer_token? }
export async function c7AddCard({ customerId, cardDetails, customerToken = '' }) {
  const res = await apiFetch(`${C7}/customer/credit-card/add/`, {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, card_details: cardDetails, customer_token: customerToken }),
  });
  return unwrap(await res.json());
}

// POST customer/credit-card/update/  { customer_id, card_id, payload, customer_token? }
export async function c7UpdateCard({ customerId, cardId, payload, customerToken = '' }) {
  const res = await apiFetch(`${C7}/customer/credit-card/update/`, {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, card_id: cardId, payload, customer_token: customerToken }),
  });
  return unwrap(await res.json());
}

// GET customer/address/list/?customer_id=&customer_token=  → { addresses: [...] }
export async function c7GetAddresses({ customerId, customerToken = '' }) {
  const params = new URLSearchParams({ customer_id: customerId, customer_token: customerToken });
  const res = await apiFetch(`${C7}/customer/address/list/?${params}`);
  return unwrap(await res.json());
}

// POST customer/address/add/  { customer_id, address_payload, customer_token? }
export async function c7AddAddress({ customerId, addressPayload, customerToken = '' }) {
  const res = await apiFetch(`${C7}/customer/address/add/`, {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, address_payload: addressPayload, customer_token: customerToken }),
  });
  return unwrap(await res.json());
}

// POST customer/address/update/  { customer_id, address_id, address_payload, customer_token? }
export async function c7UpdateAddress({ customerId, addressId, addressPayload, customerToken = '' }) {
  const res = await apiFetch(`${C7}/customer/address/update/`, {
    method: 'POST',
    body: JSON.stringify({
      customer_id: customerId,
      address_id: addressId,
      address_payload: addressPayload,
      customer_token: customerToken,
    }),
  });
  return unwrap(await res.json());
}

// ─── COMMERCE7: clubs ──────────────────────────────────────
// GET clubs/  → { clubs: [...] }
export async function c7GetClubs() {
  const res = await apiFetch(`${C7}/clubs/`);
  return unwrap(await res.json());
}

// GET clubs/memberships/?customer_id=  → { clubMemberships: [...] }
export async function c7GetMemberships(customerId) {
  const params = new URLSearchParams({ customer_id: customerId });
  const res = await apiFetch(`${C7}/clubs/memberships/?${params}`);
  return unwrap(await res.json());
}

// POST clubs/join/  { customer_id, payload }
export async function c7JoinClub({ customerId, payload }) {
  const res = await apiFetch(`${C7}/clubs/join/`, {
    method: 'POST',
    body: JSON.stringify({ customer_id: customerId, payload }),
  });
  return unwrap(await res.json());
}

// POST clubs/signup/create/  { club_id, customer_id, customer_token }  → signup session
export async function c7CreateSignup({ clubId, customerId, customerToken }) {
  const res = await apiFetch(`${C7}/clubs/signup/create/`, {
    method: 'POST',
    body: JSON.stringify({ club_id: clubId, customer_id: customerId, customer_token: customerToken }),
  });
  return unwrap(await res.json());
}

// GET clubs/signup/<id>/products/?customer_token=  → signup products
export async function c7GetSignupProducts({ signupId, customerToken }) {
  const params = new URLSearchParams({ customer_token: customerToken });
  const res = await apiFetch(`${C7}/clubs/signup/${encodeURIComponent(signupId)}/products/?${params}`);
  return unwrap(await res.json());
}

// POST clubs/signup/<id>/update-items/  { items, customer_token, club_id?, frequency? }
export async function c7UpdateSignupItems({ signupId, items, customerToken, clubId = '', frequency }) {
  const body = { items, customer_token: customerToken };
  if (clubId) body.club_id = clubId;
  if (frequency != null) body.frequency = frequency;
  const res = await apiFetch(`${C7}/clubs/signup/${encodeURIComponent(signupId)}/update-items/`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return unwrap(await res.json());
}

// POST clubs/signup/<id>/virtual-cart/  { club_id, items, customer_token, delivery_payload? }  → pricing
// delivery_payload (optional): {orderDeliveryMethod, pickupInventoryLocationId} or
// {orderDeliveryMethod, shipTo, billTo} — without it Commerce7 has no delivery
// info on the signup yet and won't resolve shipTo/pickupBy in the response.
export async function c7GetSignupVirtualCart({ signupId, clubId, items, customerToken, deliveryPayload }) {
  const body = { club_id: clubId, items, customer_token: customerToken };
  if (deliveryPayload) body.delivery_payload = deliveryPayload;
  const res = await apiFetch(`${C7}/clubs/signup/${encodeURIComponent(signupId)}/virtual-cart/`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return unwrap(await res.json());
}

// POST clubs/signup/<id>/checkout/  { customer_token, delivery_payload+customer_credit_card_id
//   OR checkout_payload (+ optional raw address/card for a brand-new entry) }
export async function c7SignupCheckout({
  signupId,
  customerToken,
  deliveryPayload,
  customerCreditCardId,
  checkoutPayload,
  address,
  card,
}) {
  const body = { customer_token: customerToken };
  if (deliveryPayload && customerCreditCardId) {
    body.delivery_payload = deliveryPayload;
    body.customer_credit_card_id = customerCreditCardId;
  } else {
    body.checkout_payload = checkoutPayload;
    if (address) body.address = address;
    if (card) body.card = card;
  }
  const res = await apiFetch(`${C7}/clubs/signup/${encodeURIComponent(signupId)}/checkout/`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return unwrap(await res.json());
}

// POST clubs/membership/update/  { membership_id, payload }
export async function c7UpdateMembership({ membershipId, payload }) {
  const res = await apiFetch(`${C7}/clubs/membership/update/`, {
    method: 'POST',
    body: JSON.stringify({ membership_id: membershipId, payload }),
  });
  return unwrap(await res.json());
}

// GET clubs/membership/next-shipment/?membership_id=  → next shipment
export async function c7GetNextShipment(membershipId) {
  const params = new URLSearchParams({ membership_id: membershipId });
  const res = await apiFetch(`${C7}/clubs/membership/next-shipment/?${params}`);
  return unwrap(await res.json());
}

// POST clubs/membership/cancel/  { customer_id, membership_id, cancellation_reason, cancellation_comments?, customer_token }
export async function c7CancelMembership({ customerId, membershipId, cancellationReason, cancellationComments = '', customerToken }) {
  const res = await apiFetch(`${C7}/clubs/membership/cancel/`, {
    method: 'POST',
    body: JSON.stringify({
      customer_id: customerId,
      membership_id: membershipId,
      cancellation_reason: cancellationReason,
      cancellation_comments: cancellationComments,
      customer_token: customerToken,
    }),
  });
  return unwrap(await res.json());
}

// ─── WINE DRAWER ───────────────────────────────────────────
// BE endpoint: GET /api/v1/chat/get-card-details/<unique_id>/
export async function getCardDetails(uniqueId) {
  const res = await apiFetch(`/api/v1/chat/get-card-details/${encodeURIComponent(uniqueId)}/`);
  return flattenCardDetails(unwrapNestedData(await res.json()));
}

// BE endpoint: GET /api/v1/chat/food-pairings/?task_id=&wine_key=&session_id=&refresh=
export async function getFoodPairings({ taskId, wineKey, sessionId, refresh = false }) {
  const params = new URLSearchParams({
    task_id: taskId,
    wine_key: wineKey,
    session_id: sessionId,
  });
  if (refresh) params.set('refresh', 'true');
  const res = await apiFetch(`/api/v1/chat/food-pairings/?${params}`);
  return unwrapNestedData(await res.json());
}

// BE endpoint: GET /api/v1/chat/food-pairings/recipe/?task_id=&wine_key=&dish_title=&session_id=
export async function getFoodPairingRecipe({ taskId, wineKey, dishTitle, sessionId }) {
  const params = new URLSearchParams({
    task_id: taskId,
    wine_key: wineKey,
    dish_title: dishTitle,
    session_id: sessionId,
  });
  const res = await apiFetch(`/api/v1/chat/food-pairings/recipe/?${params}`);
  return unwrapNestedData(await res.json());
}

// BE endpoint: POST /api/v1/chat/food-pairings/recipe/click/
// Body: { session_id, wine_key, dish_title }
export async function trackRecipeClick({ sessionId, wineKey, dishTitle }) {
  const res = await apiFetch('/api/v1/chat/food-pairings/recipe/click/', {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      wine_key: wineKey,
      dish_title: dishTitle,
    }),
  });
  return unwrap(await res.json());
}